import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const COP = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(n) || 0);
const num = (v) => parseFloat(v) || 0;

// Mapeo por bodega_id — igual que el ERP
const CUENTAS_PDV = {
  2: { label: 'Bancolombia · Jhon Ruiz ···8644',       metodo_pago: 'bc_jhon',    nit: '1065002659' },
  3: { label: 'Bancolombia · Betty De Vargas ···0891',  metodo_pago: 'bc_betty',   nit: '1067924655' },
  4: { label: 'Bancolombia · Gelatus SAS ···9449',      metodo_pago: 'bc_gelatus', nit: '901955872'  },
  5: { label: 'Bancolombia · Gelatus SAS ···9449',      metodo_pago: 'bc_gelatus', nit: '901955872'  },
  6: { label: 'Bancolombia · Gelatus SAS ···9449',      metodo_pago: 'bc_gelatus', nit: '901955872'  },
};
const CUENTAS_DV = {
  2: { label: 'Davivienda · Jhon Ruiz ···2452',         metodo_pago: 'dv_jhon',    nit: '1065002659' },
  3: { label: 'Davivienda · Betty De Vargas ···8204',   metodo_pago: 'dv_betty',   nit: '1067924655' },
  4: { label: 'Davivienda · Gelatus SAS ···1783',       metodo_pago: 'dv_gelatus', nit: '901955872'  },
  5: { label: 'Davivienda · Gelatus SAS ···1783',       metodo_pago: 'dv_gelatus', nit: '901955872'  },
  6: { label: 'Davivienda · Gelatus SAS ···1783',       metodo_pago: 'dv_gelatus', nit: '901955872'  },
};

const st = {
  label: { fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 6 },
  input: { width: '100%', padding: '10px 12px', border: '2px solid #E5E7EB', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  opcion: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 12, cursor: 'pointer', textAlign: 'left', width: '100%' },
};

// ─────────────────────────────────────────
// Foto: captura desde cámara
// ─────────────────────────────────────────
function CampoFoto({ foto, preview, onChange, onQuitar }) {
  return preview ? (
    <div style={{ position: 'relative' }}>
      <img src={preview} alt="Comprobante" style={{ width: '100%', borderRadius: 10, maxHeight: 200, objectFit: 'cover', border: '2px solid #10B981' }} />
      <button onClick={onQuitar} style={{ position: 'absolute', top: 8, right: 8, background: '#EF4444', color: 'white', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>✕</button>
    </div>
  ) : (
    <label style={{ display: 'block', cursor: 'pointer' }}>
      <div style={{ border: '2px dashed #D1D5DB', borderRadius: 10, padding: '22px', textAlign: 'center', background: '#F9FAFB' }}>
        <div style={{ fontSize: 32, marginBottom: 6 }}>📸</div>
        <div style={{ fontWeight: 700, color: '#374151', fontSize: 14 }}>Tomar foto</div>
        <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>Usa la cámara de tu celular</div>
      </div>
      <input type="file" accept="image/*" capture="environment" onChange={onChange} style={{ display: 'none' }} />
    </label>
  );
}

// ─────────────────────────────────────────
// Modal: registrar destino de un día
// ─────────────────────────────────────────
function ModalDestino({ fila, onClose, onGuardado }) {
  const [destino, setDestino] = useState('consignacion');
  const [banco, setBanco] = useState('bancolombia');
  const [retiradoPor, setRetiradoPor] = useState('');
  const [notas, setNotas] = useState('');
  const [foto, setFoto] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const cuentaBC = CUENTAS_PDV[fila.bodega_id] || CUENTAS_PDV[4];
  const cuentaDV = CUENTAS_DV[fila.bodega_id] || CUENTAS_DV[4];
  const cuentaSel = banco === 'bancolombia' ? cuentaBC : cuentaDV;

  const handleFoto = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setFoto(file);
    const reader = new FileReader();
    reader.onload = (ev) => setFotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleGuardar = async () => {
    if (destino === 'consignacion' && !foto) { toast.error('Toma una foto de la consignación'); return; }
    if (destino === 'retiro_gerencia' && !retiradoPor) { toast.error('Ingresa el nombre de quien retiró'); return; }
    setGuardando(true);
    try {
      const r = await API.post('/sobres', {
        cierre_caja_id: fila.cierre_id, bodega_id: fila.bodega_id, fecha: fila.fecha, monto: fila.total_efectivo,
        destino,
        cuenta_destino: destino === 'consignacion' ? cuentaSel.label : null,
        metodo_pago: destino === 'consignacion' ? cuentaSel.metodo_pago : null,
        nit_receptor: destino === 'consignacion' ? cuentaSel.nit : null,
        retirado_por: destino === 'retiro_gerencia' ? retiradoPor : null,
        notas,
      });
      if (foto && r.data?.id) {
        const fd = new FormData(); fd.append('foto', foto);
        await API.post(`/sobres/${r.data.id}/foto`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success('Sobre registrado');
      onGuardado(); onClose();
    } catch { toast.error('Error al registrar sobre'); }
    finally { setGuardando(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 -8px 40px rgba(0,0,0,0.3)' }}>
        <div style={{ background: '#1a1a2e', color: 'white', padding: '18px 20px', position: 'sticky', top: 0 }}>
          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 2 }}>💵 Registrar destino del sobre</div>
          <div style={{ fontSize: 17, fontWeight: 800 }}>🏪 {fila.bodega}</div>
          <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>
            {format(new Date(fila.fecha.slice(0, 10) + 'T12:00'), "d 'de' MMMM yyyy", { locale: es })} — {COP(fila.total_efectivo)}
          </div>
        </div>

        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={st.label}>¿Qué pasó con el efectivo?</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button onClick={() => setDestino('consignacion')} style={{ ...st.opcion, border: `2px solid ${destino === 'consignacion' ? '#10B981' : '#E5E7EB'}`, background: destino === 'consignacion' ? '#F0FDF4' : 'white' }}>
                <span style={{ fontSize: 20 }}>🏦</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>Consignación bancaria</div>
                  <div style={{ fontSize: 11, color: '#6B7280' }}>→ {cuentaBC.label}</div>
                </div>
              </button>
              <button onClick={() => setDestino('retiro_gerencia')} style={{ ...st.opcion, border: `2px solid ${destino === 'retiro_gerencia' ? '#6366F1' : '#E5E7EB'}`, background: destino === 'retiro_gerencia' ? '#EEF2FF' : 'white' }}>
                <span style={{ fontSize: 20 }}>🤝</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>Retiro por Gerencia</div>
                  <div style={{ fontSize: 11, color: '#6B7280' }}>Gerencia retiró el efectivo</div>
                </div>
              </button>
            </div>
          </div>

          {destino === 'consignacion' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <label style={st.label}>Cuenta de destino</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  {[{ v: 'bancolombia', l: '🏦 Bancolombia' }, { v: 'davivienda', l: '🏦 Davivienda' }].map((op) => (
                    <button key={op.v} onClick={() => setBanco(op.v)} style={{ flex: 1, padding: '8px 0', border: `2px solid ${banco === op.v ? '#2563EB' : '#E5E7EB'}`, borderRadius: 8, background: banco === op.v ? '#2563EB' : 'white', color: banco === op.v ? 'white' : '#374151', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>{op.l}</button>
                  ))}
                </div>
                <div style={{ fontSize: 12, color: '#059669', fontWeight: 600, background: '#F0FDF4', padding: '7px 10px', borderRadius: 8 }}>✅ {cuentaSel.label}</div>
              </div>
              <div>
                <label style={st.label}>Foto de la consignación *</label>
                <CampoFoto preview={fotoPreview} onChange={handleFoto} onQuitar={() => { setFoto(null); setFotoPreview(null); }} />
              </div>
            </div>
          ) : (
            <div>
              <label style={st.label}>¿Quién retiró? *</label>
              <input value={retiradoPor} onChange={(e) => setRetiradoPor(e.target.value)} placeholder="Nombre de quien retiró" style={st.input} />
            </div>
          )}

          <div>
            <label style={st.label}>Notas (opcional)</label>
            <textarea value={notas} onChange={(e) => setNotas(e.target.value)} rows={2} placeholder="Observaciones..." style={{ ...st.input, resize: 'none', fontFamily: 'inherit' }} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, padding: '16px 20px', borderTop: '1px solid #F3F4F6' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '12px 0', border: '2px solid #E5E7EB', borderRadius: 10, background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#374151' }}>Cancelar</button>
          <button onClick={handleGuardar} disabled={guardando} style={{ flex: 2, padding: '12px 0', border: 'none', borderRadius: 10, color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700, background: destino === 'consignacion' ? '#059669' : '#4F46E5' }}>
            {guardando ? '⏳ Guardando...' : '✓ Registrar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Modal: consignación múltiple
// ─────────────────────────────────────────
function ModalMultiple({ filasPendientes, onClose, onGuardado }) {
  const [seleccionadas, setSeleccionadas] = useState({});
  const [foto, setFoto] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [destino, setDestino] = useState('consignacion');
  const [banco, setBanco] = useState('bancolombia');
  const [retiradoPor, setRetiradoPor] = useState('');
  const [notas, setNotas] = useState('');
  const [guardando, setGuardando] = useState(false);

  const seleccionadasList = filasPendientes.filter((f) => seleccionadas[f.cierre_id]);
  const totalSeleccionado = seleccionadasList.reduce((a, f) => a + num(f.total_efectivo), 0);

  const handleFoto = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setFoto(file);
    const reader = new FileReader();
    reader.onload = (ev) => setFotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleGuardar = async () => {
    if (seleccionadasList.length === 0) { toast.error('Selecciona al menos un día'); return; }
    if (destino === 'consignacion' && !foto) { toast.error('Adjunta la foto de la consignación'); return; }
    if (destino === 'retiro_gerencia' && !retiradoPor) { toast.error('Ingresa quién retiró'); return; }
    setGuardando(true);
    try {
      const sobresCreados = [];
      for (const fila of seleccionadasList) {
        const cuentaMap = banco === 'bancolombia' ? CUENTAS_PDV : CUENTAS_DV;
        const cuenta = cuentaMap[fila.bodega_id] || CUENTAS_PDV[4];
        const r = await API.post('/sobres', {
          cierre_caja_id: fila.cierre_id, bodega_id: fila.bodega_id, fecha: fila.fecha, monto: fila.total_efectivo,
          destino,
          cuenta_destino: destino === 'consignacion' ? cuenta.label : null,
          metodo_pago: destino === 'consignacion' ? cuenta.metodo_pago : null,
          nit_receptor: destino === 'consignacion' ? cuenta.nit : null,
          retirado_por: destino === 'retiro_gerencia' ? retiradoPor : null,
          notas: notas || `Consignación múltiple — ${seleccionadasList.length} días`,
        });
        if (r.data?.id) sobresCreados.push(r.data.id);
      }
      if (foto && sobresCreados.length) {
        for (const id of sobresCreados) {
          const fd = new FormData(); fd.append('foto', foto);
          await API.post(`/sobres/${id}/foto`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
      }
      toast.success(`✅ ${seleccionadasList.length} sobres registrados`);
      onGuardado(); onClose();
    } catch (e) { toast.error(e.response?.data?.error || 'Error al registrar'); }
    finally { setGuardando(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 520, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ background: '#1a1a2e', color: 'white', padding: '18px 20px', flexShrink: 0 }}>
          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 2 }}>🏦 Consignación múltiple</div>
          <div style={{ fontSize: 17, fontWeight: 800 }}>Registrar varios días a la vez</div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 8 }}>
              Días a incluir ({seleccionadasList.length} seleccionados)
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
              {filasPendientes.map((f) => (
                <label key={f.cierre_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: seleccionadas[f.cierre_id] ? '#EFF6FF' : '#F9FAFB', borderRadius: 8, cursor: 'pointer', border: `1px solid ${seleccionadas[f.cierre_id] ? '#BFDBFE' : '#E5E7EB'}` }}>
                  <input type="checkbox" checked={!!seleccionadas[f.cierre_id]} onChange={(e) => setSeleccionadas((s) => ({ ...s, [f.cierre_id]: e.target.checked }))} style={{ width: 16, height: 16 }} />
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#374151' }}>{format(new Date(f.fecha.slice(0, 10) + 'T12:00'), "EEE d MMM", { locale: es })}</span>
                  <span style={{ fontWeight: 800, color: '#10B981', fontSize: 12, fontFamily: 'monospace' }}>{COP(f.total_efectivo)}</span>
                </label>
              ))}
            </div>
            {seleccionadasList.length > 0 && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: '#F0FDF4', borderRadius: 8, display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ fontWeight: 600, color: '#065F46' }}>Total a consignar:</span>
                <span style={{ fontWeight: 800, color: '#059669', fontFamily: 'monospace' }}>{COP(totalSeleccionado)}</span>
              </div>
            )}
          </div>

          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 8 }}>Tipo</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[{ v: 'consignacion', l: '🏦 Consignación' }, { v: 'retiro_gerencia', l: '🤝 Retiro' }].map((op) => (
                <button key={op.v} onClick={() => setDestino(op.v)} style={{ flex: 1, padding: '10px 0', border: `2px solid ${destino === op.v ? '#1a1a2e' : '#E5E7EB'}`, borderRadius: 10, background: destino === op.v ? '#1a1a2e' : 'white', color: destino === op.v ? 'white' : '#374151', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>{op.l}</button>
              ))}
            </div>
          </div>

          {destino === 'consignacion' ? (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 8 }}>Foto comprobante *</div>
              <CampoFoto preview={fotoPreview} onChange={handleFoto} onQuitar={() => { setFoto(null); setFotoPreview(null); }} />
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 8 }}>Retirado por *</div>
              <input value={retiradoPor} onChange={(e) => setRetiradoPor(e.target.value)} placeholder="Nombre de quien retiró" style={st.input} />
            </div>
          )}

          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 8 }}>Notas (opcional)</div>
            <textarea value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Ej: Consignación del 3 al 6..." rows={2} style={{ ...st.input, resize: 'none', fontFamily: 'inherit' }} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, padding: '16px 20px', borderTop: '1px solid #F3F4F6', flexShrink: 0 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '12px 0', border: '2px solid #E5E7EB', borderRadius: 10, background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#374151' }}>Cancelar</button>
          <button onClick={handleGuardar} disabled={guardando || seleccionadasList.length === 0}
            style={{ flex: 2, padding: '12px 0', border: 'none', borderRadius: 10, background: seleccionadasList.length === 0 ? '#E5E7EB' : '#1a1a2e', color: seleccionadasList.length === 0 ? '#9CA3AF' : 'white', cursor: seleccionadasList.length === 0 ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700 }}>
            {guardando ? '⏳' : `✅ Registrar ${seleccionadasList.length} — ${COP(totalSeleccionado)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Tarjeta de día
// ─────────────────────────────────────────
function TarjetaDia({ fila, onRegistrar, onVerFoto }) {
  const registrado = !!fila.sobre_id;
  const sinCierre = !fila.cierre_id;
  const fotoUrl = fila.foto_consignacion ? `https://erp.gelatus.co/uploads/consignaciones/${fila.foto_consignacion}` : null;

  return (
    <div style={{ background: registrado || sinCierre ? 'white' : '#FFFBEB', borderRadius: 12, padding: '12px 14px', marginBottom: 8, border: '1px solid', borderColor: registrado ? '#F3F4F6' : sinCierre ? '#F3F4F6' : '#FDE68A' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#111827', textTransform: 'capitalize' }}>
            {format(new Date(fila.fecha.slice(0, 10) + 'T12:00'), "EEEE d 'de' MMM", { locale: es })}
          </div>
          {!sinCierre && (
            <div style={{ fontFamily: 'monospace', fontWeight: 800, color: '#10B981', fontSize: 15, marginTop: 2 }}>{COP(fila.total_efectivo)}</div>
          )}
        </div>
        {registrado ? (
          <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: '#D1FAE5', color: '#065F46', whiteSpace: 'nowrap' }}>✅ Registrado</span>
        ) : sinCierre ? (
          <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: '#F3F4F6', color: '#9CA3AF', whiteSpace: 'nowrap' }}>Sin cierre</span>
        ) : (
          <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: '#FEF3C7', color: '#92400E', whiteSpace: 'nowrap' }}>⏳ Pendiente</span>
        )}
      </div>

      {registrado && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, paddingTop: 8, borderTop: '1px solid #F3F4F6' }}>
          <div style={{ fontSize: 12, color: '#374151' }}>
            {fila.destino === 'consignacion' ? <span style={{ color: '#3B82F6', fontWeight: 600 }}>🏦 Consignación</span> : <span style={{ color: '#6366F1', fontWeight: 600 }}>🤝 {fila.retirado_por || 'Retiro gerencia'}</span>}
          </div>
          {fotoUrl && (
            <button onClick={() => onVerFoto(fotoUrl)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, padding: 0 }}>📷</button>
          )}
        </div>
      )}

      {!registrado && fila.cierre_id && (
        <button onClick={() => onRegistrar(fila)} style={{ width: '100%', marginTop: 10, padding: '9px 0', border: '2px solid #1a1a2e', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#1a1a2e' }}>
          + Registrar destino
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// PANTALLA — /sobres
// ─────────────────────────────────────────
export default function Sobres() {
  const { usuario } = useAuth();
  const esAdmin = ['gerencia', 'administracion'].includes(usuario?.rol);

  const hoy = new Date();
  const [mes, setMes] = useState(hoy.getMonth() + 1);
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [bodegaId, setBodegaId] = useState('');
  const [bodegaNombre, setBodegaNombre] = useState('');
  const [bodegas, setBodegas] = useState([]);
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [modalFila, setModalFila] = useState(null);
  const [modalMultiple, setModalMultiple] = useState(false);
  const [fotoVisor, setFotoVisor] = useState(null);

  const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  // Bodega
  useEffect(() => {
    if (esAdmin) {
      API.get('/bodegas').then((r) => {
        const pvs = r.data.filter((b) => b.tipo === 'punto_venta');
        setBodegas(pvs);
        if (pvs.length) { setBodegaId(String(pvs[0].id)); setBodegaNombre(pvs[0].nombre); }
      }).catch(() => toast.error('No se pudieron cargar los puntos de venta'));
    } else {
      API.get('/app/mi-perfil').then((r) => {
        if (r.data?.bodega_id) { setBodegaId(String(r.data.bodega_id)); setBodegaNombre(r.data.bodega_nombre || ''); }
        else toast.error('No tienes un punto de venta asignado');
      }).catch(() => toast.error('Error cargando tu perfil'));
    }
  }, []);

  const cambiarBodega = (b) => { setBodegaId(String(b.id)); setBodegaNombre(b.nombre); };

  const cargar = () => {
    if (!bodegaId) return;
    setCargando(true);
    API.get('/sobres', { params: { mes, anio, bodega_id: bodegaId } })
      .then((r) => setFilas(r.data))
      .catch(() => toast.error('Error cargando sobres'))
      .finally(() => setCargando(false));
  };
  useEffect(cargar, [mes, anio, bodegaId]);

  const pendientes = filas.filter((f) => !f.sobre_id && f.cierre_id);
  const totalPendiente = pendientes.reduce((a, f) => a + num(f.total_efectivo), 0);

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#111827' }}>💵 Sobres</h1>
        <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>{bodegaNombre}</div>
      </div>

      {esAdmin && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, overflowX: 'auto', paddingBottom: 4 }}>
          {bodegas.map((b) => (
            <button key={b.id} onClick={() => cambiarBodega(b)} style={{ padding: '6px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, border: `2px solid ${String(bodegaId) === String(b.id) ? '#1a1a2e' : '#E5E7EB'}`, background: String(bodegaId) === String(b.id) ? '#1a1a2e' : 'white', color: String(bodegaId) === String(b.id) ? 'white' : '#374151', whiteSpace: 'nowrap', flexShrink: 0 }}>{b.nombre}</button>
          ))}
        </div>
      )}

      {/* Mes / Año */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <select value={mes} onChange={(e) => setMes(Number(e.target.value))} style={{ ...st.input, flex: 2 }}>
          {meses.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <select value={anio} onChange={(e) => setAnio(Number(e.target.value))} style={{ ...st.input, flex: 1 }}>
          {[2025, 2026, 2027].map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      {pendientes.length > 0 && (
        <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
          <div style={{ fontWeight: 800, color: '#92400E', fontSize: 13 }}>⚠️ {pendientes.length} sobre{pendientes.length > 1 ? 's' : ''} sin registrar</div>
          <div style={{ fontSize: 12, color: '#B45309', marginTop: 2 }}>Total: <strong>{COP(totalPendiente)}</strong></div>
        </div>
      )}

      {pendientes.length > 1 && (
        <button onClick={() => setModalMultiple(true)} style={{ width: '100%', padding: '12px', border: 'none', borderRadius: 12, background: '#1E40AF', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700, marginBottom: 14 }}>
          🏦 Registrar consignación múltiple ({pendientes.length} días)
        </button>
      )}

      {cargando ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>Cargando...</div>
      ) : filas.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9CA3AF' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
          <div style={{ fontWeight: 600 }}>Sin cierres en {meses[mes - 1]} {anio}</div>
        </div>
      ) : (
        <div>
          {filas.map((fila, i) => (
            <TarjetaDia key={i} fila={fila} onRegistrar={setModalFila} onVerFoto={setFotoVisor} />
          ))}
        </div>
      )}

      {modalFila && <ModalDestino fila={modalFila} onClose={() => setModalFila(null)} onGuardado={cargar} />}
      {modalMultiple && <ModalMultiple filasPendientes={pendientes} onClose={() => setModalMultiple(false)} onGuardado={cargar} />}

      {fotoVisor && (
        <div onClick={() => setFotoVisor(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ position: 'relative', maxWidth: '92vw', maxHeight: '85vh' }}>
            <img src={fotoVisor} alt="Consignación" style={{ maxWidth: '92vw', maxHeight: '80vh', borderRadius: 12, objectFit: 'contain' }} />
            <button onClick={() => setFotoVisor(null)} style={{ position: 'absolute', top: -14, right: -8, background: '#EF4444', color: 'white', border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
}
